import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import dayjs from 'dayjs'
import specializedReportService from '@api/v1/specializedReportService'
import reportJobService from '@api/v1/reportJobService'
import useLatestRun from '@hooks/useLatestRun'
import SpecializedReportShell from '@components/reports/SpecializedReportShell'
import SpecializedFilterBar from '@components/reports/SpecializedFilterBar'
import { objectField, dateRangeField } from '@components/reports/specializedFilterFields'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import useSavedReportView from '@components/reports/useSavedReportView'
import { describeFilters, dateRangePart } from '@components/reports/reportFilterSummary'
import CsvReportGrid from '@components/reports/CsvReportGrid'

const TYPE_USERS = 1
const TYPE_COMPANIES = 3
const TYPE_MATTERS = 1001
const CASE_COMPANY_LOOKUP = 1010

const startOfLastMonth = () => dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
const endOfLastMonth = () => dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')

// `emptyValue: null` is required, not cosmetic: the wire coerces empty dates to
// null because the C# field is DateTime? and '' does not bind (same trap CC-49949
// pinned for Revenue & Commissions).
const FIELDS = [
  objectField({ key: 'client', label: 'Client', typeId: TYPE_COMPANIES, idKey: 'clientId', nameKey: 'clientName' }),
  objectField({ key: 'matter', label: 'Matter', typeId: TYPE_MATTERS, idKey: 'billingObjectId', nameKey: 'billingObjectName' }),
  objectField({ key: 'respAtty', label: 'Responsible Attorney', typeId: TYPE_USERS, idKey: 'userId', nameKey: 'userName' }),
  dateRangeField({
    key: 'entryDates', label: 'Billing Entry Date Range',
    startKey: 'entryDateStart', endKey: 'entryDateEnd', emptyValue: null, omitAll: true,
  }),
]

const makeDefaults = () => ({
  clientId: null, clientName: '',
  userId: null, userName: '',
  billingObjectId: null, billingObjectName: '',
  entryDateStart: startOfLastMonth(),
  entryDateEnd: endOfLastMonth(),
})

// The wire object. `billableObjectType` and `billableObjectClientFieldId` have NO
// control — the latter names the lookup field the proc joins the client on, so
// dropping it silently unscopes the client filter.
const toWire = (f) => ({
  clientId: f.clientId,
  clientName: f.clientName,
  userId: f.userId,
  userName: f.userName,
  billingObjectId: f.billingObjectId,
  billingObjectName: f.billingObjectName,
  entryDateStart: f.entryDateStart || null,
  entryDateEnd: f.entryDateEnd || null,
  billableObjectType: TYPE_MATTERS,
  billableObjectClientFieldId: CASE_COMPANY_LOOKUP,
})

// Module-level so useLatestRun's `run` keeps one identity (spec 131).
const runErrorMessage = (err) => err.message || 'Report failed'

const BillingRealizationReport = ({ reportId, name = 'Billing Realization Report', summary, jobId }) => {
  const [filters, setFilters] = useState(makeDefaults)
  // Spec 131: only the NEWEST run may write the rows, the spinner, the error or
  // the documents' criteria (the split button's mid-run "Run report now" is also
  // disabled now; this gate is the structural guarantee).
  const {
    running, error, ran, runKey, run: startRun, invalidate: invalidateRun,
  } = useLatestRun({ initialRunning: !!jobId, errorMessage: runErrorMessage })
  // CC-50013 (G3): saved views. The hook carries the grid's page state, so
  // the menu cannot be mounted without it (the CC-49732 defect).
  const view = useSavedReportView(setFilters)
  // CC-50029: exports state the period they cover.
  const filtersSummary = useMemo(() => describeFilters(dateRangePart(filters.entryDateStart, filters.entryDateEnd)), [filters])
  const [rows, setRows] = useState([])
  const [savedAsOf, setSavedAsOf] = useState(null)
  // Consume-once: the first run reopens the tray's saved job; later runs are fresh.
  const jobRef = useRef(jobId || null)

  const filterPayload = useMemo(() => toWire(filters), [filters])

  const runReport = useCallback(() => {
    if (!filters.entryDateStart || !filters.entryDateEnd) {
      // Spec 131: the refusal also retires a run still in flight.
      invalidateRun({ error: 'Date range is required.' })
      return undefined
    }
    return startRun(async () => {
      const jid = jobRef.current
      jobRef.current = null // consumed — subsequent runs are fresh
      if (jid) {
        // Tray "View report" → reopen the exact saved snapshot + show its run time.
        const [st, r] = await Promise.all([reportJobService.status(jid), reportJobService.result(jid)])
        return () => { setSavedAsOf(st?.createdAt || null); setRows(r?.data?.rows || []) }
      }
      const result = await specializedReportService.billingRealization.run(filterPayload, { reportId })
      // Spec 131 decision 3: the saved-result banner clears at the COMMIT.
      return () => { setSavedAsOf(null); setRows(result) }
    }, { criteria: { filtersSummary } })
  }, [filterPayload, filters.entryDateStart, filters.entryDateEnd, reportId, startRun, invalidateRun, filtersSummary])

  // Spec 131: deferred and cancellable, so StrictMode's simulated remount starts
  // ONE mount run, not two.
  useEffect(() => {
    const t = setTimeout(() => { runReport() }, 0)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Dynamic month columns derived from row[0]
  const monthHeaders = useMemo(
    () => (rows[0]?.monthlyHeaders || []).map(String),
    [rows]
  )

  // Flatten the month-pivot into the generic { headers, rows } shape (CC-49304)
  // so it renders through the universal grid (sort/filter/show-hide/resize/persist).
  const gridData = useMemo(() => {
    const headers = ['Matter', 'Responsible Attorney', ...monthHeaders]
    const gridRows = rows.map((row) => {
      const o = {
        Matter: row.billableObjectName || '',
        'Responsible Attorney': row.responsibleName || '',
      }
      // Preserve the original multi-line month cells (whitespace-pre-line) — the
      // grid renders cell text as HTML, so map newlines to <br>.
      monthHeaders.forEach((h, j) => { o[h] = (row.monthlyBillingDates?.[j] || '').replace(/\n/g, '<br>') })
      return o
    })
    return { headers, rows: gridRows, meta: [] }
  }, [rows, monthHeaders])

  // CC-50013 G2 / CC-50148: shared metadata-driven bar (see FIELDS / toWire).
  const filterBar = (
    <SpecializedFilterBar
      fields={FIELDS}
      filters={filters}
      onFiltersChange={setFilters}
      defaults={makeDefaults()}
      onRun={runReport}
      isLoading={running}
      queue={{
        reportId,
        name,
        actionKey: 'billing-realization',
        endpoint: (f) => ({ method: 'POST', path: '/reports/billing-realization', body: toWire(f) }),
      }}
      actions={(
        <ReportViewsMenu
          storageKey="report:billing-realization"
          currentFilters={filters}
          onApply={view.applyView}
          getPageState={view.getPageState}
        />
      )}
    />
  )

  return (
    <SpecializedReportShell
      name={name} summary={summary} running={running} error={error}
      ranCriteria={ran?.criteria ?? null} runKey={runKey}
      filterBar={filterBar} filtersSummary={filtersSummary} savedAsOf={savedAsOf}
      download={(format, getToken) =>
        specializedReportService.billingRealization.download(filterPayload, format, getToken, `billing-realization.${format}`)
      }
    >
      <CsvReportGrid key={view.gridKey} data={gridData} storageKey="report:billing-realization" pageStateRef={view.pageStateRef} initialPageState={view.initialPageState} />
    </SpecializedReportShell>
  )
}

export default BillingRealizationReport
